import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { deleteUsersInSuborg, getSuborganizationById, getUsersByOrgId, getUsersInSuborg, postUsersInSuborg } from "../../api/broker";
import { iDataSuborganizacion } from "../../interfaces/SuperAdmin/Pages/iDataSuborganizacion";
import { iDataUser } from "../../interfaces/SuperAdmin/Pages/iDataUser";
import {
  iDataUserSuborg,
  UserSuborgFields,
} from "../../interfaces/SuperAdmin/Pages/iDataUserSuborg";

interface SingleSuborgContextProps {
  suborg: iDataSuborganizacion | null;
  suborgUsers: iDataUserSuborg[];
  allUsers: iDataUser[];
  selectedSuborgUsers: string[];

  isLoadingUsers: boolean;
  isLoadingAllUsers: boolean;
  isAdding: boolean;
  isDeleting: boolean;

  isDeleteModalOpen: boolean;
  isAddModalOpen: boolean;

  searchQuery: string;

  toggleAddModal: (isOpen: boolean) => void;
  toggleDeleteModal: (isOpen: boolean) => void;
  deleteSelections: (body: UserSuborgFields) => Promise<void>
  submitSelections: (body: UserSuborgFields) => Promise<void>;
  setSearchQuery: Dispatch<SetStateAction<string>>;
  setSelectedSuborgUsers: Dispatch<SetStateAction<string[]>>;
}

const SingleSuborgContext = createContext<SingleSuborgContextProps>(
  {} as SingleSuborgContextProps
);

interface Props {
  children: React.ReactNode;
}

const SingleSuborgProvider: FC<Props> = ({ children }) => {
  const { idSuborganization, idOrganization } = useParams();
  const [suborg, setSuborg] = useState<iDataSuborganizacion | null>(null);
  const [suborgUsers, setSuborgUsers] = useState<iDataUserSuborg[]>([]);
  const [selectedSuborgUsers, setSelectedSuborgUsers] = useState<string[]>([]);
  const [allUsers, setAllUsers] = useState<iDataUser[]>([]);

  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingAllUsers, setIsLoadingAllUsers] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");

  // Fetch de la suborganizacion y de sus usuarios
  const fetchInfo = async (id: string) => {
    setIsLoadingUsers(true);
    try {
      const [suborgRes, userRes] = await Promise.all([
        getSuborganizationById(id),
        getUsersInSuborg(id),
      ]);

      console.log(suborgRes);
      if (suborgRes.data.statusCode === 200) {
        setSuborg(suborgRes.data.body);
      } else {
        toast.error("Error al obtener la suborganización");
      }

      console.log(userRes);
      if (userRes.data.statusCode === 200) {
        setSuborgUsers(userRes.data.body);
      } else {
        toast.error("Error al obtener los usuarios de la suborganización");
      }
    } catch (error) {
      console.log(error);
      toast.error(
        "Error al obtener los usuarios de la suborganización o la suborganización"
      );
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Función para traer todos los usuarios de la organización de la suborg
  const fetchAllUsers = async () => {
    if (idOrganization) {
      setIsLoadingAllUsers(true);
      try {
        const res = await getUsersByOrgId(idOrganization);
        if (res.data.statusCode === 200) {
          setAllUsers(res.data.body);
        } else {
          toast.error("Error al obtener los usuarios de la organización");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al obtener los usuarios de la organización");
      } finally {
        setIsLoadingAllUsers(false);
      }
    }
  };

  // Apenas se renderize el componente, hacemos el fetch
  useEffect(() => {
    if (idSuborganization) {
      fetchInfo(idSuborganization);
    }
  }, [idSuborganization]);

  // Abrir y cerrar modal de agregar usuarios
  const toggleAddModal = (isOpen: boolean) => {
    if (isOpen && !allUsers.length) {
      fetchAllUsers();
    }
    setIsAddModalOpen(isOpen);
  };

  // Abrir y cerrar modal de eliminar usuarios
  const toggleDeleteModal = (isOpen: boolean) => {
    setIsDeleteModalOpen(isOpen);
  };

  // Quitar usuarios de la suborganización
  const deleteSelections = async (body: UserSuborgFields) => {
    setIsDeleting(true);
    try {
      const res = await deleteUsersInSuborg(body);
      console.log(res);

      if (res.data.statusCode === 200) {
        setSelectedSuborgUsers([]);
        setSuborgUsers((prev) =>
          prev.filter((item) => !body.id_usuarios.includes(item.id_usuario))
        );
        toggleDeleteModal(false);
        toast.success("Usuarios eliminados de la suborganización exitosamente");
      } else {
        toast.error("Error al eliminar los usuarios de la suborganización");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al eliminar los usuarios de la suborganización");
    } finally {
      setIsDeleting(false);
    }
  };

  // Agregar usuarios a suborganización
  const submitSelections = async (body: UserSuborgFields) => {
    setIsAdding(true);
    try {
      const res = await postUsersInSuborg(body);
      console.log(res);

      if (res.data.statusCode === 201) {
        setSuborgUsers((prev) => {
          return [...prev, ...res.data.body];
        });
        setIsAddModalOpen(false);
        toast.success("Usuarios agregados a la suborganización exitosamente");
      } else {
        toast.error("Error al agregar los usuarios a la suscripción");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al agregar los usuarios a la suscripción");
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <SingleSuborgContext.Provider
      value={{
        isLoadingUsers,
        isLoadingAllUsers,
        isDeleting,
        isAdding,
        suborg,
        allUsers,
        selectedSuborgUsers,
        suborgUsers,
        isDeleteModalOpen,
        isAddModalOpen,
        searchQuery,
        deleteSelections,
        submitSelections,
        toggleAddModal,
        toggleDeleteModal,
        setSearchQuery,
        setSelectedSuborgUsers,
      }}
    >
      {children}
    </SingleSuborgContext.Provider>
  );
};

const useSuperAdminSingleSuborg = () => useContext(SingleSuborgContext);

export { SingleSuborgProvider, useSuperAdminSingleSuborg };

